import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './App.css';
import './venueStyles.css';

// Import auth components
import { Login } from './components/auth';
import ModernLogin from './components/auth/ModernLogin';

// Import dashboard components
import DashboardLayout from './components/dashboard/DashboardLayout';
import ModernDashboardLayout from './components/dashboard/ModernDashboardLayout';
import SimpleModernLayout from './components/dashboard/SimpleModernLayout';
import WorkingLayout from './components/dashboard/WorkingLayout';
import SimpleLayout from './components/dashboard/SimpleLayout';
import Dashboard from './components/dashboard/Dashboard';
import ModernDashboard from './components/dashboard/ModernDashboard';
import FrontDeskListScreen from './components/dashboard/FrontDeskListScreen';
import { PayingGuestReservationScreen, RateManagementScreen } from './components/dashboard';
import SetupUnitsScreen from './components/setup/SetupUnitsScreen';
import SetupGroupsScreen from './components/setup/SetupGroupsScreen';
import SetupAgentsScreen from './components/setup/SetupAgentsScreen';
import SetupGuestsScreen from './components/setup/SetupGuestsScreen';
import SetupOwnerPortalScreen from './components/setup/SetupOwnerPortalScreen';
import SetupBookingEngineScreen from './components/setup/SetupBookingEngineScreen';
import SetupTimeshareScreen from './components/setup/SetupTimeshareScreen';
import SetupOwnerScreen from './components/setup/SetupOwnerScreen';
import SetupReportsScreen from './components/setup/SetupReportsScreen';
import SetupWorkOrdersScreen from './components/setup/SetupWorkOrdersScreen';
import SetupHousekeepingScreen from './components/setup/SetupHousekeepingScreen';
import SetupFrontDeskScreen from './components/setup/SetupFrontDeskScreen';
import SetupReservationsScreen from './components/setup/SetupReservationsScreen';
import SetupBarHeadersScreen from './components/setup/SetupBarHeadersScreen';
import SetupBarRatesScreen from './components/setup/SetupBarRatesScreen';

// Import guests components
import { GuestsFindScreen, GuestsListScreen, GuestsAddScreen } from './components/admin/guests';


// Import units components
import { UnitsFindScreen, UnitsListScreen, UnitsAddScreen } from './components/admin/units';

// Import agents components
import { AgentsFindScreen, AgentsListScreen, AgentsAddScreen } from './components/admin/agents';
// Import groups components
import { GroupsFindScreen, GroupsListScreen, GroupsAddScreen } from './components/admin/groups';
import { OwnersFindScreen, OwnersListScreen, OwnersAddScreen } from './components/admin/owners';
import { GLAccountsScreen } from './components/admin/gl-accounts';
import { LedgersScreen } from './components/admin/ledgers';
import { TaxesScreen } from './components/admin/taxes';
import { TrialBalanceScreen } from './components/admin/trial-balance';
import TransactionsScreen from './components/admin/transactions/TransactionsScreen';
import HousekeepingCleanStatus from './components/housekeeping/HousekeepingCleanStatus';
import UsersScreen from './components/admin/UsersScreen';

function App() {
  // Initialize theme on app load - Professional White Theme
  useEffect(() => {
    const root = document.documentElement;
    
    // Professional White Theme
    root.style.setProperty('--primary-color', '#ffffff');
    root.style.setProperty('--secondary-color', '#2c3e50');
    root.style.setProperty('--accent-color', '#3498db');
    root.style.setProperty('--text-light', '#2c3e50');
    root.style.setProperty('--text-dark', '#ffffff');
    root.style.setProperty('--border-color', '#e9ecef');
    root.style.setProperty('--background-light', '#f8f9fa');
    root.style.setProperty('--shadow-color', 'rgba(0, 0, 0, 0.1)');
    
    document.body.style.background = '#ffffff';
    document.documentElement.setAttribute('data-theme', 'professional-white');
  }, []);

  return (
    <MantineProvider>
      <Notifications />
      <Router>
        <div className="App">
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<ModernLogin />} />
          
          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<SimpleLayout />}>
            <Route index element={<ModernDashboard />} />
            <Route path="front-desk-list" element={<FrontDeskListScreen />} />
            <Route path="setup-units" element={<SetupUnitsScreen />} />
            <Route path="setup-groups" element={<SetupGroupsScreen />} />
            <Route path="setup-agents" element={<SetupAgentsScreen />} />
            <Route path="setup-guests" element={<SetupGuestsScreen />} />
            <Route path="setup-owner-portal" element={<SetupOwnerPortalScreen />} />
            <Route path="setup-booking-engine" element={<SetupBookingEngineScreen />} />
            <Route path="setup-timeshare" element={<SetupTimeshareScreen />} />
            <Route path="setup-owner" element={<SetupOwnerScreen />} />
            <Route path="setup-reports" element={<SetupReportsScreen />} />
            <Route path="setup-work-orders" element={<SetupWorkOrdersScreen />} />
            <Route path="setup-housekeeping" element={<SetupHousekeepingScreen />} />
            <Route path="setup-front-desk" element={<SetupFrontDeskScreen />} />
            <Route path="setup-reservations" element={<SetupReservationsScreen />} />
            <Route path="setup-bar-headers" element={<SetupBarHeadersScreen />} />
            <Route path="setup-bar-rates" element={<SetupBarRatesScreen />} />
            
            {/* Guests Management Routes */}
            <Route path="guests-find" element={<GuestsFindScreen />} />
            <Route path="guests-list" element={<GuestsListScreen />} />
            <Route path="guests-add" element={<GuestsAddScreen />} />
            
            {/* Units Management Routes */}
            <Route path="units-find" element={<UnitsFindScreen />} />
            <Route path="units-list" element={<UnitsListScreen />} />
            <Route path="units-add" element={<UnitsAddScreen />} />
            
                          {/* Agents Management Routes */}
              <Route path="agents-find" element={<AgentsFindScreen />} />
              <Route path="agents-list" element={<AgentsListScreen />} />
              <Route path="agents-add" element={<AgentsAddScreen />} />
              {/* Groups Management Routes */}
              <Route path="groups-find" element={<GroupsFindScreen />} />
              <Route path="groups-list" element={<GroupsListScreen />} />
              <Route path="groups-add" element={<GroupsAddScreen />} />
              
              {/* Owners Management Routes */}
              <Route path="owners-find" element={<OwnersFindScreen />} />
              <Route path="owners-list" element={<OwnersListScreen />} />
              <Route path="owners-add" element={<OwnersAddScreen />} />
              
                              {/* GL Accounts Management Routes */}
                <Route path="gl-accounts" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-find" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-list" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-add" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-change" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-delete" element={<GLAccountsScreen />} />
                <Route path="gl-accounts-adjust" element={<GLAccountsScreen />} />
                
                {/* Ledgers Management Routes */}
                <Route path="ledgers" element={<LedgersScreen />} />
                <Route path="accounting-ledgers" element={<LedgersScreen />} />
                
                {/* Trial Balance Routes */}
                <Route path="accounting-trial-balance" element={<TrialBalanceScreen />} />
                
                {/* Taxes Management Routes */}
                <Route path="taxes" element={<TaxesScreen />} />
                <Route path="taxes-list" element={<TaxesScreen />} />
                <Route path="taxes-add" element={<TaxesScreen />} />
                <Route path="taxes-change" element={<TaxesScreen />} />
                <Route path="taxes-delete" element={<TaxesScreen />} />
                
                            {/* Transactions Management Routes */}
            <Route path="transactions" element={<TransactionsScreen />} />
            <Route path="transactions-find" element={<TransactionsScreen />} />
            <Route path="transactions-list" element={<TransactionsScreen />} />
            <Route path="transactions-add" element={<TransactionsScreen />} />
            <Route path="transactions-change" element={<TransactionsScreen />} />
            <Route path="transactions-delete" element={<TransactionsScreen />} />
            
            {/* Housekeeping Routes */}
            <Route path="housekeeping-clean-status" element={<HousekeepingCleanStatus />} />
            
            {/* Reservation Routes */}
            <Route path="reservations-paying-guest" element={<PayingGuestReservationScreen />} />
            
            {/* Rate Management Routes */}
            <Route path="setup-rates-management" element={<RateManagementScreen />} />
            
            {/* Users Management Routes */}
            <Route path="setup-users" element={<UsersScreen />} />
          </Route>
            
          {/* Catch all route - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </MantineProvider>
  );
}

export default App;